import {
  isPublicHttpsNewsUrl,
  type NewsThreadMetadata,
} from "@moderation-contract/news-thread-core";
import { forwardRef, useCallback, useState } from "react";
import noImage from "@/assets/img/no-image.svg";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import {
  createNewsArticleImageMediator,
  isNewsArticleImageEvent,
  type NewsArticleImageEvent,
} from "@/shared/ui/media/newsArticleImageMediator";

export interface NewsArticleLinkProps
  extends Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    "href" | "target" | "rel" | "referrerPolicy"
  > {
  news: NewsThreadMetadata;
}

export const NewsArticleLink: React.ForwardRefExoticComponent<
  NewsArticleLinkProps & React.RefAttributes<HTMLAnchorElement>
> = forwardRef(function NewsArticleLink(
  { news, children, ...anchorProps }: NewsArticleLinkProps,
  ref: React.Ref<HTMLAnchorElement>,
): React.ReactElement {
  return (
    <a
      {...anchorProps}
      ref={ref}
      href={resolvePublicNewsUrl(news.articleUrl) ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
    >
      {children}
    </a>
  );
});

export interface NewsArticleImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt"> {
  news: NewsThreadMetadata;
  alt: string;
}

export const NewsArticleImage: React.ForwardRefExoticComponent<
  NewsArticleImageProps & React.RefAttributes<HTMLImageElement>
> = forwardRef(function NewsArticleImage(
  { alt, news, onError, ...imageProps }: NewsArticleImageProps,
  ref: React.Ref<HTMLImageElement>,
): React.ReactElement {
  const imageUrl = resolvePublicNewsUrl(news.imageUrl);
  const [mediator] = useState(createNewsArticleImageMediator);
  useHierarchyLifetime(mediator);
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (!isNewsArticleImageEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const handleImageError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>): void => {
      if (imageUrl === null) return;
      const failedEvent: NewsArticleImageEvent = {
        type: "news-article-image/failed",
        imageUrl,
      };
      node.dispatch(failedEvent);
      onError?.(event);
    },
    [imageUrl, node, onError],
  );
  const shouldUseNewsImage =
    imageUrl !== null && state.failedImageUrl !== imageUrl;

  return (
    <ComponentScope node={node}>
      <NewsArticleImageView
        {...imageProps}
        ref={ref}
        src={shouldUseNewsImage ? imageUrl : noImage}
        alt={alt}
        referrerPolicy="no-referrer"
        onError={shouldUseNewsImage ? handleImageError : undefined}
      />
    </ComponentScope>
  );
});

interface NewsArticleImageViewProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt"> {
  src: string;
  alt: string;
}

const NewsArticleImageView: React.ForwardRefExoticComponent<
  NewsArticleImageViewProps & React.RefAttributes<HTMLImageElement>
> = forwardRef(function NewsArticleImageView(
  { alt, src, ...imageProps }: NewsArticleImageViewProps,
  ref: React.Ref<HTMLImageElement>,
): React.ReactElement {
  return <img {...imageProps} ref={ref} src={src} alt={alt} />;
});

function resolvePublicNewsUrl(value: string): string | null {
  if (!isPublicHttpsNewsUrl(value)) return null;
  try {
    const url = new URL(value);
    return url.href;
  } catch {
    return null;
  }
}
